import {
  PAIR_AMOUNT_DECIMALS,
  USD_DECIMALS,
} from '@ui/constants/numbersDecimals';
import { GenericTradeParams, TpSlValueState } from '@ui/features/Trade/types';
import { toBigValue } from '@ui/lib/bignumber';
import {
  getActiveSlOrders,
  getActiveTpOrders,
} from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/selectors';
import {
  DecreaseOrderKind,
  DecreaseOrderRow,
} from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/types';
import type { DecreaseOrderUpdateInput } from '@ui/services/contract/hooks/TradingPortalFacet/useBatchUpdateDecreaseOrders/useBatchUpdateDecreaseOrders';
import type { Address } from '@ui/types/alias';
import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';

export type LegacyUpdateTradeTpSlParams = readonly [Address, bigint, bigint];

export type UpdateTradeTpSlParams = {
  updateOrders: readonly DecreaseOrderUpdateInput[];
  cancelOrderHashes: readonly `0x${string}`[];
  createOrders: readonly {
    kind: DecreaseOrderKind;
    triggerPrice: bigint;
    closeQty: bigint;
    broker: number;
  }[];
};

const ZERO_ORDER_HASH =
  '0x0000000000000000000000000000000000000000000000000000000000000000';

const getPriceValue = (value: string) => {
  if (isEmpty(value) || isNaN(parseFloat(value))) return 0n;

  return toBigValue(value, USD_DECIMALS);
};

const getIsSameOrderInput = (
  order: DecreaseOrderRow,
  triggerPrice: bigint,
  closeQty: bigint,
  broker: number,
) => {
  return (
    order.triggerPrice === triggerPrice &&
    order.closeQty === closeQty &&
    order.broker === broker
  );
};

export const useUpdateTradeTpAndSlParams = ({
  genericTradeParams,
  activeDecreaseOrders,
  tp,
  sl,
}: {
  tp: TpSlValueState;
  sl: TpSlValueState;
  activeDecreaseOrders?: DecreaseOrderRow[];
} & GenericTradeParams):
  | LegacyUpdateTradeTpSlParams
  | UpdateTradeTpSlParams
  | undefined => {
  return useMemo(() => {
    if (!genericTradeParams.isMergedPosition) {
      return [
        genericTradeParams.id,
        getPriceValue(tp.priceValue),
        getPriceValue(sl.priceValue),
      ] as const;
    }

    const activeTpOrders = getActiveTpOrders(activeDecreaseOrders);
    const activeSlOrders = getActiveSlOrders(activeDecreaseOrders);
    const firstTpOrder = activeTpOrders[0];
    const firstSlOrder = activeSlOrders[0];
    const closeQty = toBigValue(genericTradeParams.size, PAIR_AMOUNT_DECIMALS);
    const tpPriceValue = getPriceValue(tp.priceValue);
    const slPriceValue = getPriceValue(sl.priceValue);
    const updateOrders: DecreaseOrderUpdateInput[] = [];
    const cancelOrderHashes: `0x${string}`[] = [];
    const createOrders: {
      kind: DecreaseOrderKind;
      triggerPrice: bigint;
      closeQty: bigint;
      broker: number;
    }[] = [];

    const appendKindUpdate = ({
      kind,
      priceValue,
      firstOrder,
      extraOrders,
    }: {
      kind: DecreaseOrderKind;
      priceValue: bigint;
      firstOrder?: DecreaseOrderRow;
      extraOrders: DecreaseOrderRow[];
    }) => {
      const broker = firstOrder?.broker ?? 0;

      if (priceValue > 0n) {
        if (firstOrder) {
          if (!getIsSameOrderInput(firstOrder, priceValue, closeQty, broker)) {
            updateOrders.push({
              orderHash: firstOrder.orderHash,
              triggerPrice: priceValue,
              closeQty,
              broker,
            });
          }
        } else {
          createOrders.push({
            kind,
            triggerPrice: priceValue,
            closeQty,
            broker,
          });
        }
      } else if (firstOrder && firstOrder.orderHash !== ZERO_ORDER_HASH) {
        cancelOrderHashes.push(firstOrder.orderHash);
      }

      extraOrders.forEach((order) => {
        if (order.orderHash !== ZERO_ORDER_HASH) {
          cancelOrderHashes.push(order.orderHash);
        }
      });
    };

    appendKindUpdate({
      kind: DecreaseOrderKind.TP,
      priceValue: tpPriceValue,
      firstOrder: firstTpOrder,
      extraOrders: activeTpOrders.slice(1),
    });
    appendKindUpdate({
      kind: DecreaseOrderKind.SL,
      priceValue: slPriceValue,
      firstOrder: firstSlOrder,
      extraOrders: activeSlOrders.slice(1),
    });

    if (
      updateOrders.length === 0 &&
      cancelOrderHashes.length === 0 &&
      createOrders.length === 0
    ) {
      return undefined;
    }

    return {
      updateOrders,
      cancelOrderHashes,
      createOrders,
    };
  }, [
    activeDecreaseOrders,
    genericTradeParams.id,
    genericTradeParams.isMergedPosition,
    genericTradeParams.size,
    sl.priceValue,
    tp.priceValue,
  ]);
};

export const getIsDecreaseOrderUpdateParams = (
  params: LegacyUpdateTradeTpSlParams | UpdateTradeTpSlParams,
): params is UpdateTradeTpSlParams => !Array.isArray(params);
